import * as yup from "yup";
import {
  CachedEventPulls,
  PredictiveTraitArtifactsQuery,
  useDeletePredictorMutation,
} from "src/graphql";
import { captureException } from "src/lib/monitoring";
import type { FilterRestrictions } from "src/components/explore/restrictions";
import { isPresent } from "src/types/utils";
import { useCallback } from "react";
import {
  ColumnType,
  RelativeTimestampOperators,
  TimeType,
  TraitType,
} from "src/types/visual";
import {
  isIntervalType,
  isTimestampGranularityTimeType,
} from "@hightouch/lib/query/visual/types";
import {
  DEFAULT_CATALOG_TOP_K,
  PredictorOutcomeAggregation,
  PredictorOutcomeType,
  PredictorStatus,
  PredictorType,
  type PredictorOutcomeDefinition,
} from "@hightouch/lib/predictive-traits/types";
import demoDataJson from "@hightouch/lib/predictive-traits/demo-data/default.json";
import catalogRecDemoJson from "@hightouch/lib/predictive-traits/demo-data/catalog-recommendations.json";
import lookalikeDemoJson from "@hightouch/lib/predictive-traits/demo-data/lookalike.json";
import valueOutcomeDemoJson from "@hightouch/lib/predictive-traits/demo-data/value-outcome.json";

/**
 * Predictive event filters are compiled once into cached event pull SQL at
 * save time, so they can't reference the current time or other traits.
 */
export const PREDICTIVE_EVENT_FILTER_RESTRICTIONS: FilterRestrictions = {
  denied: ["relative-dates", "trait-comparisons"],
};

/** Cached event pulls only retain this many days of history, so predictive
 * eligibility windows can't reach further back. Mirrors the backend
 * `PREDICTOR_DEFAULTS.lookbackDays`. Note: the backend allows per-predictor
 * `lookbackDays` overrides that the UI doesn't know about; those predictors
 * fall back to this default here, and the backend mapping enforces the real
 * per-predictor cap. */
export const PREDICTIVE_CACHE_LOOKBACK_DAYS = 365;

/**
 * Predictive eligibility filters are evaluated against cached events with a
 * feature definition fixed at save time, so they only support parent-model
 * properties and backward-looking windows on the predictor's key events,
 * reaching back no further than the cache retains.
 */
export const PREDICTIVE_ELIGIBILITY_RESTRICTIONS: FilterRestrictions = {
  denied: [
    "traits",
    "audiences",
    "journeys",
    "related-models",
    "non-key-events",
    "funnels",
    "window-directions",
  ],
  maxRelativeWindowDays: PREDICTIVE_CACHE_LOOKBACK_DAYS,
};

/** Catalog eligibility compiles as a live warehouse query, so the cached-event limits above don't apply.
 * Traits, audiences, and journeys stay denied because every audience that uses the trait runs this filter. */
export const CATALOG_ELIGIBILITY_RESTRICTIONS: FilterRestrictions = {
  denied: ["traits", "audiences", "journeys", "related-models", "funnels"],
};

/** Recommendations a catalog predictor generates per profile. */
export const getCatalogTopK = (predictorConfig: unknown): number => {
  const topK = isRecord(predictorConfig)
    ? predictorConfig.catalogTopK
    : undefined;
  return typeof topK === "number" && Number.isInteger(topK) && topK > 0
    ? topK
    : DEFAULT_CATALOG_TOP_K;
};

const isCatalogRecDemo = (predictor: { config?: any } | null | undefined) =>
  predictor?.config?.is_demo &&
  predictor?.config?.demo_data?.predictorType ===
    PredictorType.CATALOG_RECOMMENDATION;

const isLookalikeDemo = (predictor: { config?: any } | null | undefined) =>
  predictor?.config?.is_demo &&
  predictor?.config?.demo_data?.predictorType === PredictorType.LOOKALIKE;

// Numeric-outcome demos are propensity-type predictors whose stashed outcome
// carries a value definition, so they are told apart by the definition rather
// than by `demo_data.predictorType`.
const isValueOutcomeDemo = (predictor: { config?: any } | null | undefined) =>
  Boolean(predictor?.config?.is_demo) &&
  parseOutcomeDefinition(predictor?.config?.demo_data?.outcome?.definition)
    ?.type === PredictorOutcomeType.VALUE;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const RELATIVE_TIMESTAMP_OPERATORS = new Set<string>(
  RelativeTimestampOperators,
);

export const relativeDateFilterErrorMessage =
  'Relative date filters (e.g. "within the last 30 days") are not supported in predictive event filters. Use absolute dates instead.';

/**
 * Predictive event filters are compiled once into cached event pulls, so they
 * can't reference the current time. Detects now-relative pieces of an event
 * filter: relative timestamp operators, relative interval values, event time
 * windows, and funnel conditions, recursing into nested conditions.
 */
export const containsRelativeDateCondition = (node: unknown): boolean => {
  if (!isRecord(node)) {
    return false;
  }
  if (node.window || node.funnelCondition) {
    return true;
  }
  if (
    typeof node.operator === "string" &&
    RELATIVE_TIMESTAMP_OPERATORS.has(node.operator)
  ) {
    return true;
  }
  if (isIntervalType(node.value)) {
    return true;
  }
  // Relative before/after/between values. Time-of-day and day-of-week
  // granularities also use the relative time type but don't reference the
  // current time, so they stay allowed.
  if (
    node.timeType === TimeType.Relative &&
    !isTimestampGranularityTimeType(
      isRecord(node.propertyOptions)
        ? node.propertyOptions.timestampGranularity
        : undefined,
    )
  ) {
    return true;
  }
  const children = [
    ...(Array.isArray(node.conditions) ? node.conditions : []),
    ...(Array.isArray(node.subconditions) ? node.subconditions : []),
  ];
  return children.some(containsRelativeDateCondition);
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Labels for the value-outcome aggregation picker. Rendered from the shared
 * backend enum (the trainer's source of truth for supported aggregations),
 * not from the audience trait `TraitType` options.
 */
export const OUTCOME_AGGREGATION_LABELS: Record<
  PredictorOutcomeAggregation,
  string
> = {
  [PredictorOutcomeAggregation.SUM]: "Sum",
  [PredictorOutcomeAggregation.COUNT]: "Count",
  [PredictorOutcomeAggregation.MAX]: "Max",
};

export const OUTCOME_AGGREGATION_OPTIONS = Object.values(
  PredictorOutcomeAggregation,
).map((aggregation) => ({
  value: aggregation,
  label: OUTCOME_AGGREGATION_LABELS[aggregation],
}));

/**
 * Select options for the numeric columns of an outcome event, for the
 * value-outcome column picker. Mirrors the numeric aggregation column filter
 * used for predictive input events in `pages/schema/predictive/event-filter-list.tsx`.
 */
export const getNumericColumnOptions = (
  columns: Array<{
    name: string;
    alias?: string | null;
    type?: string | null;
    custom_type?: string | null;
  }>,
): Array<{ label: string; value: string }> =>
  columns
    .filter(
      (col) =>
        col.type === ColumnType.Number ||
        col.custom_type === ColumnType.Number ||
        col.type === ColumnType.BigInt ||
        col.custom_type === ColumnType.BigInt,
    )
    .map((col) => ({
      label: col.alias || col.name,
      value: col.name,
    }));

const isOutcomeAggregation = (
  value: unknown,
): value is PredictorOutcomeAggregation =>
  typeof value === "string" &&
  (Object.values(PredictorOutcomeAggregation) as string[]).includes(value);

/**
 * Narrows the loosely-typed `predictor_outcomes.definition` jsonb into a
 * `PredictorOutcomeDefinition`. Null (the stored value for occurrence
 * outcomes) and unrecognized shapes both map to null.
 */
export const parseOutcomeDefinition = (
  value: unknown,
): PredictorOutcomeDefinition | null => {
  if (!isRecord(value)) {
    return null;
  }
  if (value.type === PredictorOutcomeType.OCCURRENCE) {
    return { type: PredictorOutcomeType.OCCURRENCE };
  }
  if (
    value.type === PredictorOutcomeType.VALUE &&
    typeof value.value_column === "string" &&
    isOutcomeAggregation(value.aggregation)
  ) {
    return {
      type: PredictorOutcomeType.VALUE,
      value_column: value.value_column,
      aggregation: value.aggregation,
    };
  }
  return null;
};

/** The value-outcome fields of the trait form's outcome state, mirroring the
 * optional `type` / `valueColumn` / `aggregation` on `PredictorOutcomeInput`. */
export type OutcomeTypeFields = {
  type: PredictorOutcomeType;
  valueColumn?: string;
  aggregation?: PredictorOutcomeAggregation;
};

/**
 * Maps a stored `predictor_outcomes.definition` value into the camelCase
 * outcome fields used by the trait form and `PredictorOutcomeInput`.
 */
export const getOutcomeTypeFields = (
  definition: unknown,
): OutcomeTypeFields => {
  const parsed = parseOutcomeDefinition(definition);
  if (parsed?.type === PredictorOutcomeType.VALUE) {
    return {
      type: PredictorOutcomeType.VALUE,
      valueColumn: parsed.value_column,
      aggregation: parsed.aggregation,
    };
  }
  return { type: PredictorOutcomeType.OCCURRENCE };
};

/**
 * The reverse of `getOutcomeTypeFields`: builds the definition described by
 * the form's outcome fields, or null while the value config is incomplete
 * (and always null for occurrence outcomes).
 */
export const outcomeFieldsToDefinition = (outcome: {
  type?: string | null;
  valueColumn?: string | null;
  aggregation?: string | null;
}): PredictorOutcomeDefinition | null =>
  outcome.type === PredictorOutcomeType.VALUE &&
  outcome.valueColumn &&
  isOutcomeAggregation(outcome.aggregation)
    ? {
        type: PredictorOutcomeType.VALUE,
        value_column: outcome.valueColumn,
        aggregation: outcome.aggregation,
      }
    : null;

/**
 * The resolver rejects `valueColumn`/`aggregation` on occurrence outcomes, so
 * strip fields left over from toggling the outcome type in the form before
 * building the mutation input. `undefined` values are dropped by JSON
 * serialization, so occurrence payloads match the pre-value-outcome shape.
 */
export const normalizeOutcomeInput = <
  T extends {
    type?: string | null;
    valueColumn?: string | null;
    aggregation?: string | null;
  },
>(
  outcome: T,
): T =>
  outcome.type === PredictorOutcomeType.VALUE
    ? outcome
    : {
        ...outcome,
        type: undefined,
        valueColumn: undefined,
        aggregation: undefined,
      };

const keyEventPullConfigSchema = yup
  .object({
    relationship_id: yup
      .mixed()
      .test(
        "is-string-or-number",
        "Must be a string or number",
        (value) => typeof value === "string" || typeof value === "number",
      )
      .required(),
    filter: yup.object(),
    aggregations: yup
      .object({
        numericColumns: yup
          .array()
          .of(yup.string().required())
          .nullable()
          .optional(),
        categoricalColumns: yup
          .array()
          .of(yup.string().required())
          .nullable()
          .optional(),
      })
      .nullable()
      .optional(),
  })
  .required();

/** Events are transformed into event pull objects in the backend, so they need some frontend handling */
export const getKeyEventsFromPulls = (
  events?: {
    name: string;
    cached_event_pull: Pick<CachedEventPulls, "config">;
  }[],
) => {
  if (!events) return [];
  return (
    events
      .map((event) => {
        const data = getEventFromPull(event.cached_event_pull);
        if (data) {
          return { ...data, name: event.name };
        }
        return undefined;
      })
      .filter(isPresent) ?? []
  );
};

export const getEventFromPull = (pull?: Pick<CachedEventPulls, "config">) => {
  if (!pull) return undefined;
  const config = pull.config.relationship;
  if (!keyEventPullConfigSchema.isValidSync(config)) {
    const errMsg = "Unrecognized event pull config shape in predictor";
    const exception = new Error(errMsg);
    console.warn(errMsg);
    captureException(exception);
    return undefined;
  }
  return {
    name: "",
    relationshipId: String(config.relationship_id),
    filter: config.filter,
    aggregations: config.aggregations,
  };
};

export const useDeletePredictor = () => {
  const deletePredictor = useDeletePredictorMutation();
  return useCallback(
    async (predictorId: string) => {
      const result = await deletePredictor.mutateAsync({
        input: { predictorId },
      });

      if (result.deletePredictor.__typename === "DeletePredictorError") {
        throw new Error(result.deletePredictor.error);
      }

      return result.deletePredictor;
    },
    [deletePredictor],
  );
};

export const getQuantileData = (data?: PredictiveTraitArtifactsQuery) => {
  if (!data) return [];

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON
  if (predictor?.config?.is_demo) {
    if (isCatalogRecDemo(predictor)) {
      return [];
    }
    const demoJson = isLookalikeDemo(predictor)
      ? lookalikeDemoJson
      : isValueOutcomeDemo(predictor)
        ? valueOutcomeDemoJson
        : demoDataJson;
    return Array.isArray(demoJson.quantiles)
      ? (demoJson.quantiles as { score: number; percentile: number }[])
      : [];
  }

  // Fall back to real data from artifacts
  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  if (!Array.isArray(metadata?.quantiles)) {
    return [];
  }

  return metadata.quantiles.flatMap((quantile) => {
    if (
      !isRecord(quantile) ||
      !isFiniteNumber(quantile.score) ||
      !isFiniteNumber(quantile.percentile)
    ) {
      return [];
    }

    return [{ score: quantile.score, percentile: quantile.percentile }];
  });
};

export const getPredictorNumUsers = (data?: PredictiveTraitArtifactsQuery) => {
  if (!data) return 0;

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON
  if (predictor?.config?.is_demo) {
    if (isCatalogRecDemo(predictor)) {
      return catalogRecDemoJson.n_users ?? 0;
    }
    if (isLookalikeDemo(predictor)) {
      return lookalikeDemoJson.num_users ?? 0;
    }
    if (isValueOutcomeDemo(predictor)) {
      return valueOutcomeDemoJson.num_users ?? 0;
    }
    return demoDataJson.num_users ?? 0;
  }

  // Fall back to real data from runs
  const metadata = predictor?.predictor_runs[0]?.metadata;
  return metadata?.num_users ?? 0;
};

export const getLastRunDate = (data?: PredictiveTraitArtifactsQuery) => {
  if (!data) return undefined;

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON
  if (predictor?.config?.is_demo) {
    if (isCatalogRecDemo(predictor)) {
      return catalogRecDemoJson.finished_at;
    }
    if (isLookalikeDemo(predictor)) {
      return lookalikeDemoJson.finished_at;
    }
    if (isValueOutcomeDemo(predictor)) {
      return valueOutcomeDemoJson.finished_at;
    }
    return demoDataJson.finished_at;
  }

  // Fall back to real data from runs
  const run = predictor?.predictor_runs[0];
  return run?.finished_at;
};

export const quantileXKey = "percentile" as const;
export const quantileYKey = "score" as const;

const getLookalikeArtifactMetadata = (data?: PredictiveTraitArtifactsQuery) => {
  const predictor = data?.trait_definitions_by_pk?.predictor;

  if (isLookalikeDemo(predictor)) {
    return lookalikeDemoJson as unknown as Record<string, unknown>;
  }

  if (!isLookalikeAudience(data)) {
    return undefined;
  }

  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  return isRecord(metadata) ? metadata : undefined;
};

export type LookalikeTrainInfo = {
  /** Source audience users the model learned from, after seed sampling. */
  numBaseAudienceUsers: number | null;
  /** Full source audience size before seed sampling. */
  numBaseAudienceUsersTotal: number | null;
  numEligibleUsers: number | null;
  numFeatures: number | null;
  numPcaComponents: number | null;
};

const toFiniteOrNull = (value: unknown): number | null =>
  isFiniteNumber(value) ? value : null;

export const getLookalikeTrainInfo = (
  data?: PredictiveTraitArtifactsQuery,
): LookalikeTrainInfo | null => {
  const metadata = getLookalikeArtifactMetadata(data);
  if (!metadata) {
    return null;
  }

  const numBaseAudienceUsers = toFiniteOrNull(metadata.num_base_audience_users);
  const numBaseAudienceUsersTotal = toFiniteOrNull(
    metadata.num_base_audience_users_total,
  );
  const numEligibleUsers = toFiniteOrNull(metadata.num_eligible_users);
  const numFeatures = toFiniteOrNull(metadata.num_features);
  const numPcaComponents = toFiniteOrNull(metadata.num_pca_components);

  if (
    numBaseAudienceUsers === null &&
    numBaseAudienceUsersTotal === null &&
    numEligibleUsers === null &&
    numFeatures === null &&
    numPcaComponents === null
  ) {
    return null;
  }

  return {
    numBaseAudienceUsers,
    numBaseAudienceUsersTotal,
    numEligibleUsers,
    numFeatures,
    numPcaComponents,
  };
};

export const getLookalikeAuc = (data?: PredictiveTraitArtifactsQuery) => {
  const metadata = getLookalikeArtifactMetadata(data);
  return toFiniteOrNull(metadata?.auc);
};

export type LookalikeCutoffMetric = {
  k: number;
  value: number;
};

const parseCutoffMetrics = (record: unknown): LookalikeCutoffMetric[] => {
  if (!isRecord(record)) {
    return [];
  }

  return Object.entries(record)
    .flatMap(([k, value]) => {
      const numericK = Number(k);
      if (!Number.isFinite(numericK) || !isFiniteNumber(value)) {
        return [];
      }

      return [{ k: numericK, value }];
    })
    .sort((left, right) => left.k - right.k);
};

export const getLookalikePrecisionAtK = (
  data?: PredictiveTraitArtifactsQuery,
) => {
  const metadata = getLookalikeArtifactMetadata(data);
  return parseCutoffMetrics(metadata?.precision_at_k);
};

export const getLookalikeLiftAtK = (data?: PredictiveTraitArtifactsQuery) => {
  const metadata = getLookalikeArtifactMetadata(data);
  return parseCutoffMetrics(metadata?.lift_at_k);
};

export const getLookalikeRecallAtK = (data?: PredictiveTraitArtifactsQuery) => {
  const metadata = getLookalikeArtifactMetadata(data);
  return parseCutoffMetrics(metadata?.recall_at_k);
};

export const predictorIsTraining = (trait?: {
  type: string;
  predictor?: { status?: string; error_message?: string | null } | null;
}) => {
  if (predictorIsErrored(trait)) {
    return false;
  }

  return (
    trait?.type === TraitType.Predictive &&
    isPredictorInProgressStatus(trait?.predictor?.status)
  );
};

export const PREDICTOR_TOTAL_STAGES = 3;
export const PREDICTOR_STAGE_CHECKPOINTS = [
  "Prepare data",
  "Train model",
  "Run inference",
] as const;

type PredictorInProgressStatus =
  | PredictorStatus.INITIALIZING
  | PredictorStatus.INGESTING_EVENT_DATA
  | PredictorStatus.BUILDING_TRAINING_DATA
  | PredictorStatus.BUILT_TRAINING_DATA
  | PredictorStatus.TRAINING;

type PredictorInProgressStage = {
  stage: 1 | 2 | 3;
  label: string;
  tooltip: string;
};

// Bucket the backend `PredictorStatus` values into the 3 user-facing
// checkpoints. "Run inference" isn't reachable from the status enum today
// (backend flips `TRAINING` -> `LIVE` directly), so no status maps to stage 3.
const PREDICTOR_STAGE_MAP: Record<
  PredictorInProgressStatus,
  PredictorInProgressStage
> = {
  [PredictorStatus.INITIALIZING]: {
    stage: 1,
    label: "Preparing data",
    tooltip: "Pulling event data from your warehouse",
  },
  [PredictorStatus.INGESTING_EVENT_DATA]: {
    stage: 1,
    label: "Preparing data",
    tooltip: "Pulling event data from your warehouse",
  },
  [PredictorStatus.BUILDING_TRAINING_DATA]: {
    stage: 2,
    label: "Training model",
    tooltip: "Training the ML model and generating scores",
  },
  [PredictorStatus.BUILT_TRAINING_DATA]: {
    stage: 2,
    label: "Training model",
    tooltip: "Training the ML model and generating scores",
  },
  [PredictorStatus.TRAINING]: {
    stage: 2,
    label: "Training model",
    tooltip: "Training the ML model and generating scores",
  },
};

const isPredictorInProgressStatus = (
  status?: string | null,
): status is PredictorInProgressStatus =>
  Boolean(
    status && Object.hasOwn(PREDICTOR_STAGE_MAP, status as PredictorStatus),
  );

export type PredictorUiStatus =
  | { kind: "live" }
  | { kind: "warning"; errorMessage: string }
  | ({
      kind: "in_progress";
      totalStages: number;
      checkpoints: readonly string[];
      progressPercent: number;
    } & PredictorInProgressStage)
  | { kind: "unknown"; label: string; tooltip: string }
  | { kind: "failed"; errorMessage: string | null };

const getPredictorStageProgressPercent = (
  stage: PredictorInProgressStage["stage"],
): number => ((stage - 1) / (PREDICTOR_TOTAL_STAGES - 1)) * 100;

export const getPredictorUiStatus = (predictor?: {
  status?: string | null;
  error_message?: string | null;
}): PredictorUiStatus => {
  const status = predictor?.status ?? "";
  const errorMessage = predictor?.error_message ?? null;

  if (status === PredictorStatus.LIVE && errorMessage) {
    return { kind: "warning", errorMessage };
  }

  if (status === PredictorStatus.LIVE) {
    return { kind: "live" };
  }

  if (status === PredictorStatus.ERRORED || errorMessage) {
    return { kind: "failed", errorMessage };
  }

  if (!isPredictorInProgressStatus(status)) {
    return {
      kind: "unknown",
      label: "Status updating",
      tooltip: "We’re refreshing the latest predictor status.",
    };
  }

  const stage = PREDICTOR_STAGE_MAP[status];
  return {
    kind: "in_progress",
    totalStages: PREDICTOR_TOTAL_STAGES,
    checkpoints: PREDICTOR_STAGE_CHECKPOINTS,
    progressPercent: getPredictorStageProgressPercent(stage.stage),
    ...stage,
  };
};

export const predictorIsLive = (trait?: {
  type: string;
  predictor?: { status?: string } | null;
}) =>
  trait?.type === TraitType.Predictive && trait?.predictor?.status === "live";

export const predictorIsErrored = (trait?: {
  type: string;
  predictor?: { status?: string; error_message?: string | null } | null;
}) =>
  trait?.type === TraitType.Predictive &&
  (trait?.predictor?.status === "errored" ||
    Boolean(trait?.predictor?.error_message));

export const getRocAucScore = (data?: PredictiveTraitArtifactsQuery) => {
  if (!data) return null;

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON. Regression
  // demos have no classification scores.
  if (predictor?.config?.is_demo) {
    if (
      isCatalogRecDemo(predictor) ||
      isLookalikeDemo(predictor) ||
      isValueOutcomeDemo(predictor)
    ) {
      return null;
    }
    const trainingResults = Array.isArray(demoDataJson.training_results)
      ? demoDataJson.training_results
      : [];
    return trainingResults[0]?.scores?.roc_auc ?? null;
  }

  // Fall back to real data from artifacts
  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  const trainingResults = Array.isArray(metadata?.training_results)
    ? metadata.training_results
    : [];
  return trainingResults[0]?.scores?.roc_auc ?? null;
};

export const getFeatureScores = (
  data?: PredictiveTraitArtifactsQuery,
): Array<[string, number]> => {
  if (!data) return [];

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON
  if (predictor?.config?.is_demo) {
    if (isCatalogRecDemo(predictor) || isLookalikeDemo(predictor)) {
      return [];
    }
    const demoJson = isValueOutcomeDemo(predictor)
      ? valueOutcomeDemoJson
      : demoDataJson;
    return Array.isArray(demoJson.feature_scores)
      ? (demoJson.feature_scores as Array<[string, number]>)
      : [];
  }

  // Fall back to real data from artifacts
  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  const featureScores = Array.isArray(metadata?.feature_scores)
    ? (metadata.feature_scores as Array<[string, number]>)
    : [];
  return featureScores;
};

export type HumanizedFeature = {
  label: string;
  // "outcome-inverse" = past instances of the outcome event for a churn
  // predictor (predicting absence of the event); rendered with a different
  // tag than a regular outcome since the prediction direction is flipped.
  kind: "event" | "outcome" | "outcome-inverse" | "attribute";
  subtitle?: string;
  raw: string;
};

export type FeatureNameContext = {
  eventNameByPullId: Map<string, string>;
  eventNameByRelationshipId: Map<string, string>;
  outcomePullId?: string | null;
  outcomeRelationshipId?: string | null;
  isChurn?: boolean;
};

// Raw feature names produced by the predictive trainer follow these shapes
// (see decision_engine/predictive/features.py), where <prefix> is
// event_<relationshipId>_cached_<cachedEventPullId> for key events and
// target for the outcome event:
//   <prefix>(_<col>)?_ewma_(count|sum)   recency-weighted count or column sum
//   <prefix>_<col>=<value>_ewma_count    recency-weighted count where <col> is <value>
//   <prefix>_<col>_rank<n>               the user's n-th most frequent <col> value
// Plus seasonality columns (month, season, quarter) and raw user-attribute
// columns (e.g. gender, profile_brand). The non-greedy column captures rely
// on the trailing anchors, which hold for any realistic column name that
// doesn't itself end in one of these suffixes.
const EVENT_FEATURE_PREFIX_PATTERN =
  /^event_(\d+)_cached_([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(_.*)$/;

const TARGET_FEATURE_PREFIX_PATTERN = /^target(_.*)$/;

const EWMA_SUFFIX_PATTERN = /^(?:_+(.+?))?_+ewma_(count|sum)$/;

// Vocabulary values are sanitized to [0-9A-Za-z_.-] before they land in a
// column name. The last "=" separates the raw column name from the value.
const VALUE_COUNT_SUFFIX_PATTERN = /^_+(.+)=(.+)_ewma_count$/;

const RANK_SUFFIX_PATTERN = /^_+(.+?)_+rank(\d+)$/;

const TIME_FEATURES = new Set(["month", "season", "quarter"]);

const spaceFromSnake = (raw: string): string => raw.replace(/_+/g, " ").trim();

const titleCaseFromSnake = (raw: string): string => {
  const spaced = spaceFromSnake(raw);
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

type FeatureSuffix =
  | { agg: "count" }
  | { agg: "sum"; column: string | undefined }
  | { agg: "value-count"; column: string; value: string }
  | { agg: "rank"; column: string; rank: string };

const parseFeatureSuffix = (suffix: string): FeatureSuffix | undefined => {
  // Checked before the plain EWMA shape, which would otherwise swallow
  // "<col>=<value>" as an ordinary column name.
  const valueCountMatch = suffix.match(VALUE_COUNT_SUFFIX_PATTERN);
  if (valueCountMatch?.[1] && valueCountMatch[2]) {
    return {
      agg: "value-count",
      column: valueCountMatch[1],
      value: valueCountMatch[2],
    };
  }
  const ewmaMatch = suffix.match(EWMA_SUFFIX_PATTERN);
  if (ewmaMatch) {
    return ewmaMatch[2] === "sum"
      ? { agg: "sum", column: ewmaMatch[1] }
      : { agg: "count" };
  }
  const rankMatch = suffix.match(RANK_SUFFIX_PATTERN);
  if (rankMatch?.[1] && rankMatch[2]) {
    return { agg: "rank", column: rankMatch[1], rank: rankMatch[2] };
  }
  return undefined;
};

const aggregationSubtitle = (suffix: FeatureSuffix): string => {
  switch (suffix.agg) {
    case "count":
      return "count";
    case "sum":
      return suffix.column ? spaceFromSnake(suffix.column) : "sum";
    case "value-count": {
      const value =
        suffix.value === "__other__"
          ? "Other values"
          : suffix.value === "__MISSING__"
            ? "Missing"
            : spaceFromSnake(suffix.value);
      return `${spaceFromSnake(suffix.column)}: ${value} count`;
    }
    case "rank":
      return `#${suffix.rank} ${spaceFromSnake(suffix.column)}`;
  }
};

// Names on `predictor_feature_definition_events` are sometimes empty in
// production, so we resolve in this priority order:
//   1. Per-pull name (only if non-empty)
//   2. Relationship name from the parent model (keyed by the relationship_id
//      already encoded in the feature name)
//   3. Generic "Event" fallback
const lookupNonEmpty = (
  map: Map<string, string>,
  key: string | undefined | null,
): string | undefined => {
  if (!key) return undefined;
  const value = map.get(key);
  return value && value.length > 0 ? value : undefined;
};

export const humanizeFeatureName = (
  raw: string,
  ctx: FeatureNameContext,
): HumanizedFeature => {
  const eventMatch = raw.match(EVENT_FEATURE_PREFIX_PATTERN);
  const eventSuffix = eventMatch
    ? parseFeatureSuffix(eventMatch[3] ?? "")
    : undefined;
  if (eventMatch && eventSuffix) {
    const relationshipId = eventMatch[1] ?? "";
    const pullId = eventMatch[2] ?? "";
    const label =
      lookupNonEmpty(ctx.eventNameByPullId, pullId) ??
      lookupNonEmpty(ctx.eventNameByRelationshipId, relationshipId) ??
      "Event";
    return {
      label,
      kind: "event",
      subtitle: aggregationSubtitle(eventSuffix),
      raw,
    };
  }

  const targetMatch = raw.match(TARGET_FEATURE_PREFIX_PATTERN);
  const targetSuffix = targetMatch
    ? parseFeatureSuffix(targetMatch[1] ?? "")
    : undefined;
  if (targetMatch && targetSuffix) {
    const eventName =
      lookupNonEmpty(ctx.eventNameByPullId, ctx.outcomePullId) ??
      lookupNonEmpty(ctx.eventNameByRelationshipId, ctx.outcomeRelationshipId);
    // For churn predictors, append ", Churn" so it's obvious the model is
    // predicting absence of the event, not the event itself. The Outcome
    // badge color (green vs orange) does the rest of the work; the row's
    // historical-vs-future framing is explained in the card description.
    let label: string;
    if (eventName) {
      label = ctx.isChurn ? `${eventName}, Churn` : eventName;
    } else {
      label = ctx.isChurn ? "Outcome event, Churn" : "Outcome event";
    }
    // For outcome rows, the recency-weighted count is what gets binarized
    // into the training target, so call it that explicitly.
    const subtitle =
      targetSuffix.agg === "count"
        ? "binarized count"
        : aggregationSubtitle(targetSuffix);
    return {
      label,
      kind: ctx.isChurn ? "outcome-inverse" : "outcome",
      subtitle,
      raw,
    };
  }

  if (TIME_FEATURES.has(raw)) {
    return {
      label: titleCaseFromSnake(raw),
      kind: "attribute",
      raw,
    };
  }

  return {
    label: titleCaseFromSnake(raw),
    kind: "attribute",
    raw,
  };
};

// Pulls a relationship id from the loosely-typed cached_event_pull config.
// Returns it as a string so it can be used as a map key alongside the
// relationship ids exposed via segments.relationships.id.
const getRelationshipIdFromPullConfig = (
  config: unknown,
): string | undefined => {
  if (!isRecord(config)) return undefined;
  const relationship = config.relationship;
  if (!isRecord(relationship)) return undefined;
  const id = relationship.relationship_id;
  if (typeof id === "string" && id.length > 0) return id;
  if (typeof id === "number") return String(id);
  return undefined;
};

export const buildFeatureNameContext = (
  data?: PredictiveTraitArtifactsQuery,
): FeatureNameContext => {
  const trait = data?.trait_definitions_by_pk;
  const predictor = trait?.predictor;
  const events =
    predictor?.predictor_feature_definition
      ?.predictor_feature_definition_events ?? [];

  const eventNameByPullId = new Map<string, string>();
  const eventNameByRelationshipId = new Map<string, string>();

  // Parent-model relationships are the source of truth for event names —
  // `predictor_feature_definition_events.name` is empty for many older
  // predictors. Seed the relationship map first.
  const relationships = trait?.segment?.relationships ?? [];
  for (const r of relationships) {
    if (r.id != null && r.name) {
      eventNameByRelationshipId.set(String(r.id), r.name);
    }
  }

  for (const event of events) {
    if (event.cached_event_pull_id && event.name) {
      eventNameByPullId.set(event.cached_event_pull_id, event.name);
    }
    const relId = getRelationshipIdFromPullConfig(
      event.cached_event_pull?.config,
    );
    if (relId && event.name && !eventNameByRelationshipId.has(relId)) {
      eventNameByRelationshipId.set(relId, event.name);
    }
  }

  // Demo predictors have no outcome pull; their outcome relationship lives in
  // the stashed demo outcome instead.
  const outcomePull = predictor?.predictor_outcome?.cached_event_pull;
  const demoOutcomeRelationshipId =
    predictor?.config?.demo_data?.outcome?.relationship_id;
  return {
    eventNameByPullId,
    eventNameByRelationshipId,
    outcomePullId: outcomePull?.id ?? null,
    outcomeRelationshipId:
      getRelationshipIdFromPullConfig(outcomePull?.config) ??
      (demoOutcomeRelationshipId != null
        ? String(demoOutcomeRelationshipId)
        : null),
    isChurn: getIsChurnPredictor(data),
  };
};

export const getLogLoss = (data?: PredictiveTraitArtifactsQuery) => {
  if (!data) return null;

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON. Regression
  // demos have no classification scores.
  if (predictor?.config?.is_demo) {
    if (
      isCatalogRecDemo(predictor) ||
      isLookalikeDemo(predictor) ||
      isValueOutcomeDemo(predictor)
    ) {
      return null;
    }
    const trainingResults = Array.isArray(demoDataJson.training_results)
      ? demoDataJson.training_results
      : [];
    return trainingResults[0]?.scores?.neg_log_loss ?? null;
  }

  // Fall back to real data from artifacts
  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  const trainingResults = Array.isArray(metadata?.training_results)
    ? metadata.training_results
    : [];
  return trainingResults[0]?.scores?.neg_log_loss ?? null;
};

export const getAveragePrecision = (data?: PredictiveTraitArtifactsQuery) => {
  if (!data) return null;

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Check for demo predictor - use static demo data from JSON. Regression
  // demos have no classification scores.
  if (predictor?.config?.is_demo) {
    if (
      isCatalogRecDemo(predictor) ||
      isLookalikeDemo(predictor) ||
      isValueOutcomeDemo(predictor)
    ) {
      return null;
    }
    const trainingResults = Array.isArray(demoDataJson.training_results)
      ? demoDataJson.training_results
      : [];
    return trainingResults[0]?.scores?.average_precision ?? null;
  }

  // Fall back to real data from artifacts
  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  const trainingResults = Array.isArray(metadata?.training_results)
    ? metadata.training_results
    : [];
  return trainingResults[0]?.scores?.average_precision ?? null;
};

export const getIsChurnPredictor = (
  data?: PredictiveTraitArtifactsQuery,
): boolean => {
  const predictor = data?.trait_definitions_by_pk?.predictor;

  const runMetadata = predictor?.predictor_runs[0]?.metadata;
  if (runMetadata?.config?.target_event?.churn_inactivity_days != null) {
    return true;
  }

  return false;
};

/**
 * The outcome definition of the trait's predictor; null for occurrence
 * outcomes (stored as a null `definition`) and for catalog/lookalike
 * predictors, which have no value-outcome support. Demo predictors have no
 * predictor_outcome row and keep the definition in `config.demo_data`.
 */
export const getOutcomeDefinition = (
  data?: PredictiveTraitArtifactsQuery,
): PredictorOutcomeDefinition | null => {
  const predictor = data?.trait_definitions_by_pk?.predictor;
  if (predictor?.config?.is_demo) {
    return parseOutcomeDefinition(
      predictor.config?.demo_data?.outcome?.definition,
    );
  }
  return parseOutcomeDefinition(predictor?.predictor_outcome?.definition);
};

export const getIsValueOutcome = (
  data?: PredictiveTraitArtifactsQuery,
): boolean => getOutcomeDefinition(data)?.type === PredictorOutcomeType.VALUE;

export type DecileScore = {
  /** 1-10; decile 10 holds the highest predicted values. */
  decile: number;
  meanPredicted: number;
  meanObserved: number;
  samples: number;
};

/**
 * Predicted-vs-observed values by decile of the held-out test split, from the
 * accepted training result's quantile scores (`calculate_quantile_score` in
 * the trainer). Numeric-outcome demos read them from static demo data; other
 * demo predictors and predictors trained before the trainer emitted quantile
 * scores have none.
 */
export const getPredictedVsObservedDeciles = (
  data?: PredictiveTraitArtifactsQuery,
): DecileScore[] => {
  const predictor = data?.trait_definitions_by_pk?.predictor;
  if (!predictor) {
    return [];
  }

  if (predictor.config?.is_demo) {
    if (!isValueOutcomeDemo(predictor)) {
      return [];
    }
    return parseDecileScores(
      valueOutcomeDemoJson.training_results[0]?.quantile_scores,
    );
  }

  const metadata = predictor.predictor_artifacts[0]?.metadata;
  const trainingResults = Array.isArray(metadata?.training_results)
    ? metadata.training_results
    : [];
  return parseDecileScores(trainingResults[0]?.quantile_scores);
};

const parseDecileScores = (quantileScores: unknown): DecileScore[] => {
  if (!Array.isArray(quantileScores)) {
    return [];
  }

  return quantileScores
    .flatMap((entry): DecileScore[] => {
      if (
        !isRecord(entry) ||
        !isFiniteNumber(entry.quantile) ||
        !isFiniteNumber(entry.mean_true) ||
        !isFiniteNumber(entry.mean_pred) ||
        !isFiniteNumber(entry.samples)
      ) {
        return [];
      }
      return [
        {
          decile: entry.quantile + 1,
          meanPredicted: entry.mean_pred,
          meanObserved: entry.mean_true,
          samples: entry.samples,
        },
      ];
    })
    .sort((a, b) => a.decile - b.decile);
};

export type CumulativeGainsPoint = {
  /** Share of users targeted, ranked by predicted value (0-1). Doubles as
   * the random-targeting baseline. */
  populationShare: number;
  /** Share of observed value captured (0-1). */
  capturedValueShare: number;
};

/**
 * Cumulative gains from predicted-value deciles on the held-out test set.
 * Walks from the highest predicted decile to the lowest. Each point is: if
 * you target the top X% of users by predicted value, you capture Y% of all
 * observed value. Includes the origin (0, 0). Empty when deciles are missing
 * or no value was observed.
 */
export const getCumulativeGains = (
  deciles: DecileScore[],
): CumulativeGainsPoint[] => {
  if (deciles.length === 0) {
    return [];
  }
  const totalSamples = deciles.reduce((sum, d) => sum + d.samples, 0);
  const totalValue = deciles.reduce(
    (sum, d) => sum + d.meanObserved * d.samples,
    0,
  );
  if (totalSamples <= 0 || totalValue <= 0) {
    return [];
  }

  const fromHighest = [...deciles].sort((a, b) => b.decile - a.decile);
  let runningSamples = 0;
  let runningValue = 0;
  const points: CumulativeGainsPoint[] = [
    { populationShare: 0, capturedValueShare: 0 },
  ];
  for (const decile of fromHighest) {
    runningSamples += decile.samples;
    runningValue += decile.meanObserved * decile.samples;
    points.push({
      populationShare: runningSamples / totalSamples,
      capturedValueShare: runningValue / totalValue,
    });
  }
  return points;
};

export type ConfusionMatrix = {
  truePositives: number;
  falsePositives: number;
  trueNegatives: number;
  falseNegatives: number;
};

export const getConfusionMatrix = (
  data?: PredictiveTraitArtifactsQuery,
): ConfusionMatrix | null => {
  if (!data) return null;

  const predictor = data?.trait_definitions_by_pk?.predictor;

  // Demo predictors don't have confusion matrix data
  if (predictor?.config?.is_demo) {
    return null;
  }

  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  const trainingResults = Array.isArray(metadata?.training_results)
    ? metadata.training_results
    : [];
  const scores = trainingResults[0]?.scores;

  if (
    scores?.true_positives == null ||
    scores?.false_positives == null ||
    scores?.true_negatives == null ||
    scores?.false_negatives == null
  ) {
    return null;
  }

  return {
    truePositives: scores.true_positives,
    falsePositives: scores.false_positives,
    trueNegatives: scores.true_negatives,
    falseNegatives: scores.false_negatives,
  };
};

export type GainsPoint = {
  /** Share of the audience targeted, taking the highest-scoring users first. */
  audienceShare: number;
  /** Share of every positive outcome in the test split reached at this depth. */
  capturedShare: number;
  /** How many more positives this reaches than targeting the same number of
   * users at random. 1 means the model adds nothing. */
  lift: number;
};

/**
 * Cumulative gains — "what share of converters do I reach by targeting the top
 * N%" — derived from the held-out test deciles.
 *
 * Every decile covers an equal share of the population, because the trainer
 * bins on row index or, when correcting downsampling bias, on cumulative IPW
 * weight. A decile's observed rate is therefore directly proportional to the
 * positives it holds, so the deciles can be summed without reweighting. Note
 * that `samples` is a raw row count and must NOT be used as that weight: under
 * IPW the equal-weight bins hold unequal numbers of rows.
 */
export const buildCumulativeGainsCurve = (
  deciles: DecileScore[],
): GainsPoint[] => {
  if (deciles.length === 0) {
    return [];
  }

  const totalRate = deciles.reduce((sum, point) => sum + point.meanObserved, 0);

  // No positives anywhere in the test split leaves nothing to capture, and
  // every share would divide by zero.
  if (totalRate <= 0) {
    return [];
  }

  const highestScoringFirst = [...deciles].sort((a, b) => b.decile - a.decile);

  let cumulativeRate = 0;
  return highestScoringFirst.map((point, index) => {
    cumulativeRate += point.meanObserved;
    const audienceShare = (index + 1) / highestScoringFirst.length;
    const capturedShare = cumulativeRate / totalRate;
    return {
      audienceShare,
      capturedShare,
      lift: capturedShare / audienceShare,
    };
  });
};

/**
 * Share of positive outcomes in the held-out test split.
 *
 * Prefers the confusion matrix cells because they are counted on exactly the
 * set that AUC, average precision, and log loss are scored on. `label_frequency`
 * is only a fallback: it has a different denominator (the whole downsampled
 * training matrix rather than the test split), so the two can disagree when
 * outcome rates trend over time.
 */
export const getTestSetBaseRate = (
  data?: PredictiveTraitArtifactsQuery,
): number | null => {
  const confusionMatrix = getConfusionMatrix(data);

  if (confusionMatrix) {
    const { truePositives, falsePositives, trueNegatives, falseNegatives } =
      confusionMatrix;
    const total =
      truePositives + falsePositives + trueNegatives + falseNegatives;
    if (total > 0) {
      return (truePositives + falseNegatives) / total;
    }
  }

  const predictor = data?.trait_definitions_by_pk?.predictor;
  if (predictor?.config?.is_demo) {
    return null;
  }

  const labelFrequency =
    predictor?.predictor_artifacts[0]?.metadata?.label_frequency;
  return isFiniteNumber(labelFrequency) ? labelFrequency : null;
};

export const isCatalogRecommendation = (
  data?: PredictiveTraitArtifactsQuery,
): boolean => {
  return (
    data?.trait_definitions_by_pk?.predictor?.type ===
    PredictorType.CATALOG_RECOMMENDATION
  );
};

export const isLookalikeAudience = (
  data?: PredictiveTraitArtifactsQuery,
): boolean => {
  return (
    data?.trait_definitions_by_pk?.predictor?.type === PredictorType.LOOKALIKE
  );
};

export type CatalogMetrics = {
  hitRate: number;
  mrr: number;
  ndcg: number;
  k: number;
};

export const getCatalogMetrics = (
  data?: PredictiveTraitArtifactsQuery,
): CatalogMetrics | null => {
  if (!data) return null;
  const predictor = data?.trait_definitions_by_pk?.predictor;

  if (isCatalogRecDemo(predictor)) {
    const cm = catalogRecDemoJson.catalog_metrics;
    return { hitRate: cm.hit_rate, mrr: cm.mrr, ndcg: cm.ndcg, k: cm.k };
  }

  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  const cm = metadata?.catalog_metrics;
  if (
    cm == null ||
    cm.hit_rate == null ||
    cm.mrr == null ||
    cm.ndcg == null ||
    cm.k == null
  ) {
    return null;
  }
  return {
    hitRate: cm.hit_rate,
    mrr: cm.mrr,
    ndcg: cm.ndcg,
    k: cm.k,
  };
};

export type CatalogTrainInfo = {
  nUsers: number;
  nItems: number;
  reconstructionError: number;
};

export const getCatalogTrainInfo = (
  data?: PredictiveTraitArtifactsQuery,
): CatalogTrainInfo | null => {
  if (!data) return null;
  const predictor = data?.trait_definitions_by_pk?.predictor;

  if (isCatalogRecDemo(predictor)) {
    return {
      nUsers: catalogRecDemoJson.n_users,
      nItems: catalogRecDemoJson.n_items,
      reconstructionError: catalogRecDemoJson.reconstruction_error,
    };
  }

  const metadata = predictor?.predictor_artifacts[0]?.metadata;
  if (
    metadata?.n_users == null ||
    metadata?.n_items == null ||
    metadata?.reconstruction_error == null
  ) {
    return null;
  }
  return {
    nUsers: metadata.n_users,
    nItems: metadata.n_items,
    reconstructionError: metadata.reconstruction_error,
  };
};
